import React from "react"
import cx from "classnames"
import { Icon } from "../icon"
import { BADGE_ICONS } from "./navigation-badge"

// =============================================================================
// Props
// =============================================================================

export type NavigationTooltipProps = {
  /** Sets the callout's icon, label, and colour. */
  type: "paid" | "beta"
  /** Top line — the nav item's tooltip description. */
  children: React.ReactNode
}

// =============================================================================
// Config
// =============================================================================

const CALLOUTS: Record<
  NavigationTooltipProps["type"],
  { label: string; className: string }
> = {
  paid: { label: "Paid feature", className: "text-blue-400" },
  beta: { label: "Beta feature", className: "text-purple-400" }
}

// =============================================================================
// Component
// =============================================================================

/**
 * Tooltip body for a paid or beta navigation item. Renders the item's
 * description on top and a coloured "Paid feature" / "Beta feature" callout
 * beneath it.
 *
 * Internal — used by `Navigation.Item` and `Navigation.GroupItem` to wrap
 * their `tooltip` string when a `badge` is in effect (own or inherited from
 * the enclosing `Navigation.Group`). Not part of the public API.
 */
export function NavigationTooltip({ type, children }: NavigationTooltipProps) {
  const callout = CALLOUTS[type]

  return (
    <div className="flex flex-col gap-2">
      <span>{children}</span>
      <span
        className={cx(
          "flex items-center gap-1 font-semibold",
          callout.className
        )}
      >
        <Icon
          name={BADGE_ICONS[type]}
          // eslint-disable-next-line rulesdir/no-deprecated-icon-size
          size="sm"
          aria-hidden
        />
        {callout.label}
      </span>
    </div>
  )
}

NavigationTooltip.displayName = "NavigationTooltip"
